import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import InstallAppButton from "./InstallAppButton";

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2 leading-none">
      <span className="font-serif-display text-[24px] lg:text-[26px] tracking-tight text-[var(--lc-ink)]">
        LONGEV
      </span>
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--lc-moss)]" />
      <span className="font-serif-display italic text-[22px] lg:text-[24px] tracking-tight text-[var(--lc-ink)]">
        circle
      </span>
    </Link>
  );
}

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/assessment", label: "Assessment" },
  { to: "/packages", label: "Packages" },
  { to: "/build", label: "Build Your Plan" },
  { to: "/about", label: "About" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { count, setOpen: setCartOpen } = useCart();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[var(--lc-cream)]/95 backdrop-blur-md border-b border-[var(--lc-line)]"
          : "bg-[var(--lc-cream)]/70 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        <div className="flex items-center justify-between h-[68px] lg:h-[76px]">
          <Logo />

          <nav className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `text-[12px] font-semibold tracking-[0.18em] uppercase transition-colors ${
                    isActive
                      ? "text-[var(--lc-moss)]"
                      : "text-[var(--lc-ink)] hover:text-[var(--lc-moss)]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3 lg:gap-5">
            <button
              onClick={() => setCartOpen(true)}
              className="relative p-2 -mr-2 hover:opacity-70 transition-opacity"
              aria-label="Cart"
            >
              <ShoppingBag className="h-5 w-5" />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-[var(--lc-moss)] text-white text-[10px] font-bold">
                  {count}
                </span>
              )}
            </button>
            <Link
              to="/book"
              className="hidden lg:inline-flex items-center px-5 py-2.5 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[12px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-moss-dark)] transition-colors"
            >
              Book Free Call
            </Link>
            <InstallAppButton className="hidden lg:inline-flex" compact />
            <button
              className="lg:hidden p-2 -mr-2"
              onClick={() => setOpen(!open)}
              aria-label="Menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[var(--lc-cream)] border-t border-[var(--lc-line)] px-5 py-6 space-y-3">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `block py-2 text-base ${isActive ? "text-[var(--lc-moss)] font-semibold" : "text-[var(--lc-ink)]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/book"
            className="block text-center px-5 py-3 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[12px] font-bold tracking-[0.18em] uppercase mt-3"
          >
            Book Free Call
          </Link>
          <div className="pt-2">
            <InstallAppButton className="w-full justify-center" />
          </div>
        </div>
      )}
    </header>
  );
}
